import { t } from "@lingui/core/macro"
import { captureRemixErrorBoundaryError } from "@sentry/remix"
import { isRouteErrorResponse, useRouteError } from "react-router"
import { useEnv } from "~/components/contexts/envs-context"
import { isTimeoutError } from "~/utils/is-timeout-error"

type UseErrorBoundaryDataOutput = {
  status: number
  heading: string
  description: string
}

const BAD_REQUEST = 400
const FORBIDDEN = 403
const INTERNAL_SERVER_ERROR = 500
const GATEWAY_TIMEOUT = 504

export function useErrorBoundaryData(): UseErrorBoundaryDataOutput {
  const error = useRouteError()
  const baseUrl = useEnv("PUBLIC_BASE_URL_HOST")
  if (baseUrl === "0.0.0.0") {
    console.error("global error:", error)
  }
  captureRemixErrorBoundaryError(error)

  const status = getErrorStatus(error)

  const heading = isRouteErrorResponse(error)
    ? error.statusText
    : getErrorHeading(status)

  const description = getErrorDescription(status)

  return {
    status,
    heading,
    description,
  }
}

function getErrorStatus(error: unknown): number {
  if (isRouteErrorResponse(error)) {
    return error.status
  }

  if (isTimeoutError(error)) {
    return GATEWAY_TIMEOUT
  }

  return INTERNAL_SERVER_ERROR
}

function getErrorHeading(status: number): string {
  if (status === GATEWAY_TIMEOUT) {
    return t`Connection timed out`
  }

  return t`Internal server error`
}

function getErrorDescription(status: number): string {
  if (status === FORBIDDEN) {
    return t`You do not have sufficient permissions to access this page.`
  }

  if (status === GATEWAY_TIMEOUT) {
    return t`It looks like things are taking longer than expected. Try again in a few minutes.`
  }

  if (status >= BAD_REQUEST && status < INTERNAL_SERVER_ERROR) {
    return t`Sorry, we couldn't find the page you were looking for. It might have been moved, deleted, or never existed.`
  }

  return t`We apologize, but something went wrong on our end. Our team has been notified and is working to fix the issue. Please try again later.`
}
